import { ArrowRightIcon } from '@phosphor-icons/react'
import { useCallback, useEffect, useRef } from 'react'

import { Avatar } from '@/components/ui/avatar'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Icon } from '@/components/ui/icon'
import { popupFactory } from '@/factories'

import * as S from './use-dirty-form-guard.styled'

export type DirtyFieldChange = {
  label: string
  prevValue: string
  nextValue: string
  imageUrl?: string
  arrayDiff?: {
    prevItems: { value: string; removed: boolean }[]
    nextItems: { value: string; added: boolean }[]
  }
}

interface DirtyFormGuardOptions {
  // Note: set when the guarded form itself lives in a popup - the confirmation is stacked on top of it
  // and, once confirmed, both are removed from the stack before `action` runs
  inPopup?: boolean
}

export const useDirtyFormGuard = (
  isDirty: boolean,
  getChanges?: () => DirtyFieldChange[],
  { inPopup = false }: DirtyFormGuardOptions = {},
) => {
  const getChangesRef = useRef(getChanges)
  getChangesRef.current = getChanges

  useEffect(() => {
    if (!isDirty) return

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }

    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const guard = useCallback(
    (action: () => void) => {
      if (!isDirty) {
        action()
        return
      }

      const changes = getChangesRef.current?.() ?? []

      popupFactory.open(
        ConfirmationPopup,
        {
          autoClose: !inPopup,
          autoWidth: changes.length > 0,
          children: changes.length ? (
            <S.Body>
              <S.Subtitle>
                The following changes will be lost if you leave without saving:
              </S.Subtitle>
              <S.TableWrapper>
                <S.Table>
                  <S.Thead>
                    <tr>
                      <S.Th>Field</S.Th>
                      <S.Th>Previous</S.Th>
                      <S.Th />
                      <S.Th>New</S.Th>
                    </tr>
                  </S.Thead>
                  <tbody>
                    {changes.map(({ label, prevValue, nextValue, imageUrl, arrayDiff }) => (
                      <S.Tr key={label}>
                        <S.FieldTd>{label}</S.FieldTd>
                        {arrayDiff ? (
                          <>
                            <S.PrevTd>
                              <S.ArrayList>
                                {arrayDiff.prevItems.map(({ value, removed }) =>
                                  removed ? (
                                    <S.RemovedItem key={value}>{value}</S.RemovedItem>
                                  ) : (
                                    <S.ArrayItem key={value}>{value}</S.ArrayItem>
                                  ),
                                )}
                              </S.ArrayList>
                            </S.PrevTd>
                            <S.ArrowTd>
                              <Icon icon={ArrowRightIcon} size="1.4rem" color="black400" />
                            </S.ArrowTd>
                            <S.NewTd>
                              <S.ArrayList>
                                {arrayDiff.nextItems.map(({ value, added }) =>
                                  added ? (
                                    <S.AddedItem key={value}>{value}</S.AddedItem>
                                  ) : (
                                    <S.ArrayItem key={value}>{value}</S.ArrayItem>
                                  ),
                                )}
                              </S.ArrayList>
                            </S.NewTd>
                          </>
                        ) : (
                          <>
                            <S.PrevTd>{prevValue}</S.PrevTd>
                            <S.ArrowTd>
                              <Icon icon={ArrowRightIcon} size="1.4rem" color="black400" />
                            </S.ArrowTd>
                            <S.NewTd>
                              {imageUrl ? (
                                <Avatar src={imageUrl} size="small" shape="square" />
                              ) : (
                                nextValue
                              )}
                            </S.NewTd>
                          </>
                        )}
                      </S.Tr>
                    ))}
                  </tbody>
                </S.Table>
              </S.TableWrapper>
            </S.Body>
          ) : (
            'You have unsaved changes. Are you sure you want to leave this section?'
          ),
          confirmText: 'Leave without saving',
          onConfirm: inPopup
            ? () => {
                popupFactory.clear()
                action()
              }
            : action,
          title: 'Unsaved changes',
          variant: 'warning',
        },
        inPopup ? { suspendCurrent: true } : undefined,
      )
    },
    [isDirty, inPopup],
  )

  return guard
}
